import React, {
  useState, useEffect, useContext,
} from 'react';
import { Box, Button, Chip } from '@mui/material';
import { UserContext } from '../../../contexts/User';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import '../settings.css';
import CustomTable from '../../../components/table/CustomTable';

const TABLE_HEADERS = [
  {
    label: 'Amount',
    key: 'amount',
    sortable: false,
  },
  {
    label: 'Plan',
    key: 'plan',
    sortable: false,
  },
  {
    label: 'Users',
    key: 'users',
    sortable: false,
  },
  {
    label: 'Discount',
    key: 'discount',
    sortable: false,
  },
  {
    label: 'Payment Method',
    key: 'paymentMethod',
    sortable: false,
  },
  {
    label: 'Customer',
    key: 'customer',
    sortable: false,
  },
  {
    label: 'Date',
    key: 'date',
    sortable: false,
  },
  {
    label: 'Status',
    key: 'status',
    sortable: false,
  },
  {
    label: 'Action',
    key: 'action',
    sortable: false,
  },
];

const CustomTableStyles = {
  container: {
    marginTop: 1,
  },
  header: {
    backgroundColor: 'var(--dark-10)',
    fontSize: '16px',
    '.MuiTableSortLabel-iconDirectionAsc': {
      transform: 'rotate(0deg) !important',
    },
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fff !important',
    },
    '&:hover': {
      backgroundColor: 'var(--dark-10) !important',
    },
  },
  cell: {
    button: {
      textWrap: 'nowrap',
      color: 'var(--dark-2)',
      borderColor: 'var(--dark-4) !important',
    },
  },
};

function History() {
  const { activeWorkspace } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const handleDownloadInvoice = async (transactionId) => {
    try {
      const response = await sendRequest({
        method: 'post',
        url: API.WORKSPACE.downloadInvoice(activeWorkspace.id, transactionId),
        responseType: 'blob',
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${transactionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  const getTransactions = async (page = 1) => {
    setIsLoading(true);
    try {
      const res = await sendRequest({
        method: 'get',
        url: `${API.WORKSPACE.getTransactions(activeWorkspace.id)}?page=${page}&limit=${pagination.limit}`,
      });

      const updatedTransactions = res.data.transactions.map((transaction) => {
        const hasDiscount = transaction.couponId && transaction.discountAmount;
        const originalAmount = hasDiscount ? transaction.originalAmount : transaction.amount;

        return {
          // id: transaction.id,
          amount: hasDiscount ? (
            <div>
              <div style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.9em' }}>
                $
                {parseFloat(originalAmount).toFixed(2)}
              </div>
              <div style={{ color: '#2E7D32', fontWeight: 600 }}>
                $
                {parseFloat(transaction.amount).toFixed(2)}
              </div>
            </div>
          ) : (
            `$${parseFloat(transaction.amount).toFixed(2)}`
          ),
          plan: transaction.planName || 'N/A',
          users: transaction.numberOfUsers || 'N/A',
          discount: hasDiscount ? (
            <div style={{ color: '#2E7D32' }}>
              <div style={{ fontWeight: 600 }}>
                {transaction.coupon?.code || 'COUPON'}
              </div>
              <div style={{ fontSize: '0.85em' }}>
                -$
                {parseFloat(transaction.discountAmount).toFixed(2)}
              </div>
            </div>
          ) : (
            <span style={{ color: '#999' }}>—</span>
          ),
          paymentMethod: transaction.paymentMethod || 'N/A',
          customer: transaction.customerEmail || transaction.customerName || 'N/A',
          date: new Date(transaction.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }),
          status: (
            <Chip
              label={transaction.status.toUpperCase()}
              size="small"
              color={
                // eslint-disable-next-line no-nested-ternary
              transaction.status === 'success' ? 'success' : transaction.status === 'failed' ? 'error' : 'default'
            }
              sx={{ fontWeight: 600 }}
            />
          ),
          action: transaction.status === 'success' ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleDownloadInvoice(transaction.id)}
              sx={{
                color: 'var(--dark-2)',
                borderColor: 'var(--dark-4)',
              }}
            >
              Download Invoice
            </Button>
          ) : (
            <span style={{ color: '#999' }}>—</span>
          ),
        };
      });

      setTransactions(updatedTransactions);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getTransactions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box className="d-flex justify-content-between align-items-center">
        <Box>
          <Box className="size-s text-600 lh-base fc-dark mb-1">
            Transaction History
          </Box>
          <Box className="size-xs text-400 lh-sm fc-dark">
            View all payment transactions and download invoices for successful payments
          </Box>
        </Box>
      </Box>
      <CustomTable
        headers={TABLE_HEADERS}
        items={transactions}
        styles={CustomTableStyles}
        loading={isLoading}
      />
    </>
  );
}

export default History;
